import React, { useState } from 'react';
import { generatePredictions, generateChildPredictions } from '../services/openaiService';
import { useApiKey } from '../contexts/ApiKeyContext';
import { 
  parseAPIResponse, 
  createPredictionObject, 
  validatePredictionCount,
  findPredictionById,
  updatePredictionById,
  addChildPredictions
} from '../utils/predictionUtils';
import PredictionCard from './PredictionCard';
import './TopicPredictionForm.css';

const TopicPredictionForm = () => {
  // Get API key from context
  const { apiKey, isConnected } = useApiKey();
  
  // State variables
  const [userInput, setUserInput] = useState('');
  const [responseType, setResponseType] = useState('optimistic');
  const [resultCount, setResultCount] = useState(5);
  const [predictions, setPredictions] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [originalInput, setOriginalInput] = useState('');
  const [expandingPredictions, setExpandingPredictions] = useState(new Set());
  const [originalResponseType, setOriginalResponseType] = useState('optimistic');
  const [originalResultCount, setOriginalResultCount] = useState(5);

  // Response type options
  const responseTypeOptions = [
    { value: 'general', label: '○ General Outcomes' },
    { value: 'optimistic', label: '✨ Optimistic Outcomes' },
    { value: 'pessimistic', label: '⚠️ Pessimistic Outcomes' },
    { value: 'similar', label: '≈ Similar to Input' },
    { value: 'dissimilar', label: '≠ Dissimilar to Input' }
  ];

  // Number of results options (1-10)
  const resultCountOptions = Array.from({ length: 10 }, (_, i) => i + 1);

  // Handle form submission
  const handleGenerate = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!userInput.trim()) {
      setError('Please enter a topic or question to explore.');
      return;
    }

    if (!isConnected || !apiKey.trim()) {
      setError('Please connect your OpenAI API key first.');
      return;
    }

    setIsGenerating(true);
    setError('');
    setPredictions([]);
    setOriginalInput(userInput);
    setExpandingPredictions(new Set());
    
    // Store original settings for child generation
    setOriginalResponseType(responseType);
    setOriginalResultCount(resultCount);

    try {
      // Generate predictions using the OpenAI service
      const rawResponse = await generatePredictions(userInput, responseType, apiKey, resultCount);
      
      // Parse the API response
      const parsedPredictions = parseAPIResponse(rawResponse);
      
      // Validate we got the expected number of predictions
      if (!validatePredictionCount(parsedPredictions, resultCount)) {
        console.warn(`Expected ${resultCount} predictions, got ${parsedPredictions.length}`);
      }

      // Convert to prediction objects
      const predictionObjects = parsedPredictions.map(text => 
        createPredictionObject(text, responseType, 1)
      );

      // Animate predictions appearing one by one
      for (const prediction of predictionObjects) {
        await new Promise(resolve => setTimeout(resolve, 300));
        setPredictions(prev => [...prev, prediction]);
      }

    } catch (error) {
      console.error('Generation error:', error);
      
      // Handle specific error types
      if (error.message.includes('401')) {
        setError('Invalid API key. Please check your OpenAI API key and try again.');
      } else if (error.message.includes('429')) {
        setError('Rate limit exceeded. Please wait a moment and try again.');
      } else if (error.message.includes('quota')) {
        setError('API quota exceeded. Please check your OpenAI account billing.');
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        setError(error.message || 'An unexpected error occurred while generating predictions.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle prediction expansion/collapse
  const toggleExpansion = async (predictionId) => {
    const prediction = findPredictionById(predictionId, predictions);
    
    if (!prediction) {
      console.error('Prediction not found:', predictionId);
      return;
    }

    // If prediction is at max level (3), don't allow expansion
    if (prediction.level >= 3) {
      return;
    }

    // If already expanding this prediction, ignore
    if (expandingPredictions.has(predictionId)) {
      return;
    }

    // If collapsing (already expanded), just toggle the state
    if (prediction.isExpanded) {
      setPredictions(prev => 
        updatePredictionById(prev, predictionId, { isExpanded: false })
      );
      return;
    }

    // If expanding and no children exist yet, generate them
    if (!prediction.isExpanded && prediction.children.length === 0) {
      // Mark as expanding
      setExpandingPredictions(prev => new Set([...prev, predictionId]));
      
      try {
        // Generate child predictions using the original response type and count
        const rawResponse = await generateChildPredictions(
          prediction.text, 
          originalResponseType, 
          apiKey, 
          originalResultCount
        );
        
        // Parse the API response
        const parsedChildPredictions = parseAPIResponse(rawResponse);
        
        // Validate we got the expected number of predictions
        if (!validatePredictionCount(parsedChildPredictions, originalResultCount)) {
          console.warn(`Expected ${originalResultCount} child predictions, got ${parsedChildPredictions.length}`);
        }

        // Convert to prediction objects with incremented level
        const childPredictionObjects = parsedChildPredictions.map(text => 
          createPredictionObject(text, originalResponseType, prediction.level + 1, predictionId)
        );

        // Add children to the parent prediction
        setPredictions(prev => 
          addChildPredictions(prev, predictionId, childPredictionObjects)
        );

      } catch (error) {
        console.error('Child generation error:', error);
        
        // Show error message but don't break the UI
        setError(`Failed to generate child predictions: ${error.message}`);
        
        // Clear error after 5 seconds
        setTimeout(() => setError(''), 5000);
      } finally {
        // Remove from expanding set
        setExpandingPredictions(prev => {
          const newSet = new Set(prev);
          newSet.delete(predictionId);
          return newSet;
        });
      }
    } else {
      // Just expanding existing children
      setPredictions(prev => 
        updatePredictionById(prev, predictionId, { isExpanded: true })
      );
    }
  };

  // Clear error when user starts typing
  const handleInputChange = (setter) => (e) => {
    setter(e.target.value);
    if (error) setError('');
  };

  // Check if form is valid for submission
  const isFormValid = isConnected && userInput.trim() !== '';

  return (
    <div className="topic-prediction-form">
      <div className="form-container">
        <header className="form-header">
          <h2>Next Topic Prediction</h2>
          <p>Generate AI-powered predictions based on your input and preferences</p>
        </header>

        <form onSubmit={handleGenerate} className="prediction-form">
          {/* Connection Status */}
          {!isConnected && (
            <div className="connection-notice">
              <span className="notice-icon">🔑</span>
              <p>Please connect your OpenAI API key above to start generating predictions.</p>
            </div>
          )}

          {/* User Input Textarea */}
          <div className="form-group">
            <label htmlFor="userInput" className="form-label">
              Your Input or Topic
            </label>
            <textarea
              id="userInput"
              value={userInput}
              onChange={handleInputChange(setUserInput)}
              placeholder="Enter your topic, question, or scenario here. For example: 'The future of remote work' or 'What happens if AI becomes more advanced?'"
              className="form-textarea"
              rows="4"
              disabled={isGenerating || !isConnected}
            />
            <p className="form-description">
              Describe the topic or scenario you'd like predictions about. Be as specific or general as you prefer.
            </p>
          </div>

          {/* Dropdowns Grid */}
          <div className="dropdowns-grid">
            {/* Response Type Dropdown */}
            <div className="form-group">
              <label htmlFor="responseType" className="form-label">
                Response Type
              </label>
              <select
                id="responseType"
                value={responseType}
                onChange={(e) => setResponseType(e.target.value)}
                className="form-select"
                disabled={isGenerating || !isConnected}
              >
                {responseTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="form-description">
                Choose the perspective or type of outcomes you want to explore.
              </p>
            </div>

            {/* Result Count Dropdown */}
            <div className="form-group">
              <label htmlFor="resultCount" className="form-label">
                Number of Results
              </label>
              <select
                id="resultCount"
                value={resultCount}
                onChange={(e) => setResultCount(parseInt(e.target.value))}
                className="form-select"
                disabled={isGenerating || !isConnected}
              >
                {resultCountOptions.map((count) => (
                  <option key={count} value={count}>
                    {count} result{count !== 1 ? 's' : ''}
                  </option>
                ))}
              </select>
              <p className="form-description">
                How many predictions would you like to generate?
              </p>
            </div>
          </div>

          {/* Generate Button */}
          <div className="form-group">
            <button
              type="submit"
              className={`generate-button ${!isFormValid ? 'disabled' : ''}`}
              disabled={!isFormValid || isGenerating}
            >
              {isGenerating ? (
                <>
                  <span className="spinner"></span>
                  Exploring predictions...
                </>
              ) : (
                'Generate Predictions'
              )}
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              <div className="error-text">{error}</div>
            </div>
          )}
        </form>

        {/* Results Section */}
        {(predictions.length > 0 || isGenerating) && (
          <div className="results-section">
            <div className="results-header">
              <h3>Generated Predictions</h3>
              {originalInput && (
                <p className="original-input">
                  Exploring: <strong>"{originalInput}"</strong>
                </p>
              )}
              {predictions.length > 0 && (
                <p className="results-summary">
                  Generated {predictions.length} {responseType} predictions
                </p>
              )}
            </div>

            {/* Loading State */}
            {isGenerating && (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Generating insights from AI...</p>
              </div>
            )}

            {/* Predictions List */}
            <div className="predictions-list">
              {predictions.map((prediction) => (
                <PredictionCard 
                  key={prediction.id} 
                  prediction={prediction} 
                  onExpand={toggleExpansion}
                  expandingPredictions={expandingPredictions}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopicPredictionForm; 